/**
 * Email Typo Detector
 * Detects common email typos and suggests corrections
 */

const COMMON_TYPOS = {
  // Gmail typos
  'gmail.con': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmaiil.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmil.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gomail.com': 'gmail.com',
  'gimail.com': 'gmail.com',
  
  // Google typos
  'google.con': 'google.com',
  'gooogle.com': 'google.com',
  'googel.com': 'google.com',
  'gogle.com': 'google.com',
  'goole.com': 'google.com',
  
  // Yahoo typos
  'yahoo.con': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yhoo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  
  // Outlook/Hotmail typos
  'outlook.con': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlok.com': 'outlook.com',
  'hotmail.con': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmil.com': 'hotmail.com',
  
  // iCloud typos
  'icloud.con': 'icloud.com',
  'iclod.com': 'icloud.com',
  'icould.com': 'icloud.com',
};

/**
 * Check email for common typos
 * @param {string} email - Email address to check
 * @returns {Object|null} - { typoDetected: true, original: string, suggested: string, confidence: 'high'|'medium' } or null
 */
export function checkEmailTypo(email) {
  if (!email || !email.includes('@')) {
    return null;
  }

  const cleanEmail = email.toLowerCase().trim();
  const parts = cleanEmail.split('@');
  
  if (parts.length !== 2) {
    return null;
  }

  const [localPart, domain] = parts;

  // Check exact domain match in typo list
  if (COMMON_TYPOS[domain]) {
    return {
      typoDetected: true,
      original: cleanEmail,
      suggested: `${localPart}@${COMMON_TYPOS[domain]}`,
      confidence: 'high'
    };
  }

  // Check for .con instead of .com
  if (domain.endsWith('.con')) {
    return {
      typoDetected: true,
      original: cleanEmail,
      suggested: `${localPart}@${domain.slice(0, -4)}.com`,
      confidence: 'high'
    };
  }

  // Check for double dots
  if (domain.includes('..')) {
    return {
      typoDetected: true,
      original: cleanEmail,
      suggested: `${localPart}@${domain.replace(/\.{2,}/g, '.')}`,
      confidence: 'high'
    };
  }

  // Check for missing dot before TLD (e.g., 'gmailcom')
  if (!domain.includes('.')) {
    return {
      typoDetected: true,
      original: cleanEmail,
      suggested: null,
      confidence: 'high',
      message: 'Email domain appears to be missing a dot (e.g., should be ".com" not "com")'
    };
  }

  // Check for multiple consecutive same characters (e.g., 'gooogle')
  const duplicatePattern = /(.)\1{2,}/;
  if (duplicatePattern.test(domain)) {
    // Try to fix by removing extra duplicates
    const fixedDomain = domain.replace(/(.)\1{2,}/g, '$1$1');
    if (['gmail.com', 'yahoo.com', 'google.com'].includes(fixedDomain)) {
      return {
        typoDetected: true,
        original: cleanEmail,
        suggested: `${localPart}@${fixedDomain}`,
        confidence: 'medium'
      };
    }
  }

  return null;
}

/**
 * Get a user-friendly message for the typo detection result
 * @param {Object} typoResult - Result from checkEmailTypo()
 * @returns {string} - User-friendly message
 */
export function getTypoMessage(typoResult) {
  if (!typoResult || !typoResult.typoDetected) {
    return '';
  }

  if (typoResult.suggested) {
    return `Did you mean "${typoResult.suggested}"?`;
  }

  return typoResult.message || 'Please double-check your email address';
}
